/**
 * Canonical filter AST for the data pipeline.
 *
 * One filter language across retrieval, processing, and group-by steps. Retrieval steps
 * translate to backend-specific query formats (e.g. CDF instance queries) and degrade
 * unsupported ops to client-side post-filters.
 */
import z from 'zod';

import { columnSchema } from '#plugin-chat/lib/data-pipeline/engine/column-ref.ts';
import { sourceRefSchema } from '#plugin-chat/lib/tool/dataset-keys.ts';

// Op-family constants. Single source of truth for which ops belong to which
// family — both the leaf schema variants below and the resolver / evaluator
// dispatch tables key off these tuples. Adding a new op means: append to the
// right tuple here, no other site needs to change.
export const EQUALITY_OPS = ['equals', 'notEquals'] as const;
export const PATTERN_OPS = ['contains', 'startsWith', 'endsWith', 'regex'] as const;
export const COMPARISON_OPS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte'] as const;
export const SET_OPS = ['in', 'notIn'] as const;
export const NULL_OPS = ['isNull', 'isNotNull'] as const;

/** Boolean group operators — used by level-1 and root filter groups. */
export const LOGIC_OPS = ['and', 'or', 'not'] as const;

/** All supported filter operations, derived from the per-family tuples above. */
export const FILTER_OPS = [...EQUALITY_OPS, ...PATTERN_OPS, ...COMPARISON_OPS, ...NULL_OPS, ...SET_OPS] as const;

export type FilterOp = (typeof FILTER_OPS)[number];

// Literal value-shape helpers — exported so the resolver can infer the
// matching TS types directly. The schema is the single source of truth for
// the wire format; the resolver and downstream consumers narrow against
// these inferred types.
export const literalStringSchema = z.object({ string: z.string() });
export const literalNumberSchema = z.object({ number: z.number() });
export const literalDateSchema = z.object({
  date: z.string().describe('ISO 8601 date string (e.g. "2024-01-15" or "2024-01-15T10:30:00Z").'),
});
export const literalArraySchema = z.object({
  array: z.array(z.union([z.string(), z.number(), z.boolean()])),
});

const caseSensitiveSchema = z
  .boolean()
  .optional()
  .describe('Case sensitivity for string comparisons. Default: false (case-insensitive).');

/**
 * Schema for leaf-level filter conditions, expressed as a union of op-family
 * variants. The LLM picks one variant by `op` (each variant constrains the op
 * to a specific subset) and fills in the value shape required by that family.
 *
 * Value-bearing ops carry `value`: either a literal (`{ string }`,
 * `{ number }`, `{ date }`, `{ array }`) or a `sourceRef` chained projection
 * (`{ inputDataset, column }`) from a previously staged dataset. The two
 * shapes are structurally distinct, so the union discriminates on field
 * presence — no extra tag needed.
 *
 * Numeric and date semantics are merged: `eq`/`neq`/`gt`/`gte`/`lt`/`lte` and
 * `between` accept either numbers or ISO 8601 date strings. The runtime
 * infers the comparison kind from the literal type — `{ number }` → numeric,
 * `{ date }` (or string bounds in `between`) → date. This collapses the old
 * `dateBefore`/`dateAfter`/`dateEquals`/`dateBetween` ops into the unified
 * comparison set; the LLM picks the op by intent (eq/lt/gt/between), not by
 * value type.
 *
 * For ref-typed target columns (`wellString`, `well`, `field`, …) `sourceRef`
 * is the **only** legitimate value source — literal `{string}` on a ref
 * column errors at resolution time. Spaces and externalIds vary by view/tenant
 * and are not safe to guess; the only valid instanceId in a filter is a
 * column projected from a dataset returned earlier in this conversation.
 *
 * For non-set ops (`equals`/`notEquals`, comparison ops) a `sourceRef`
 * projection must contain exactly one non-null value — the resolver errors
 * otherwise. `in`/`notIn` accept any number of projected values.
 *
 * Pattern-only ops (`contains`, `startsWith`, `endsWith`, `regex`) take
 * only a literal — no sourceRef alternative. Null ops take no value at all.
 *
 * Ranges are expressed as a level-1 AND of `gte` + `lte` (or `gt`/`lt` for
 * exclusive bounds) — there is no dedicated `between` op.
 */
export const leafFilterSchema = z.union([
  // 1. equals / notEquals — string/ref equality. Use sourceRef for refs;
  //    the case-sensitive flag applies to string equality.
  z.object({
    column: columnSchema,
    op: z.enum(EQUALITY_OPS).describe('String/ref equality. Use sourceRef for ref-typed columns.'),
    caseSensitive: caseSensitiveSchema,
    value: z.union([literalStringSchema, sourceRefSchema]),
  }),

  // 2. String pattern ops — literal only (these match patterns, not values,
  //    so chaining from a prior dataset doesn't apply).
  z.object({
    column: columnSchema,
    op: z.enum(PATTERN_OPS).describe('String pattern match.'),
    caseSensitive: caseSensitiveSchema,
    literal: z.object({
      string: z.string().describe('Pattern to match. For `regex`, the JavaScript regex source (e.g. "^DR\\\\d+").'),
    }),
  }),

  // 3. Comparison ops — number-or-date. Literal `{number}` → numeric
  //    comparison; literal `{date}` → date comparison (ISO 8601). sourceRef
  //    projects a single value; the projected column type determines the
  //    comparison kind.
  z.object({
    column: columnSchema,
    op: z
      .enum(COMPARISON_OPS)
      .describe('Numeric or date comparison. Provide `{number}` or `{date}` literal, or chain via sourceRef.'),
    value: z.union([literalNumberSchema, literalDateSchema, sourceRefSchema]),
  }),

  // 4. Set ops — value-or-sourceRef. The canonical chaining mechanism for
  //    ref-typed columns (`wellString`, `well`, `field`, …): use sourceRef
  //    with `column: "$instanceId"` to project a previously staged
  //    entity/hierarchy dataset's identity into this filter.
  z.object({
    column: columnSchema,
    op: z.enum(SET_OPS).describe('Set membership. Use sourceRef for ref-typed columns.'),
    caseSensitive: caseSensitiveSchema,
    value: z.union([literalArraySchema, sourceRefSchema]),
  }),

  // 5. Null ops — no value.
  z.object({
    column: columnSchema,
    op: z.enum(NULL_OPS),
  }),
]);

export type LeafFilter = z.infer<typeof leafFilterSchema>;

/** Schema for level-1 filter groups that combine leaf filters with logical operators. */
export const level1GroupSchema = z.object({
  type: z.enum(LOGIC_OPS).describe('Logical operator: and (all must match), or (any must match), not (negate all)'),
  filters: z.array(leafFilterSchema).min(1).describe('Array of leaf filters to combine'),
});

export type Level1Group = z.infer<typeof level1GroupSchema>;

/** Filter node — either a leaf condition or a level-1 group. Internal union used by {@link rootFilterSchema}. */
const filterNodeSchema = z.union([leafFilterSchema, level1GroupSchema]);

/** Schema for root-level filter with max 2 levels of nesting (root + level-1 groups). */
export const rootFilterSchema = z
  .object({
    type: z.enum(LOGIC_OPS).describe('Root-level logical operator'),
    filters: z.array(filterNodeSchema).min(1).describe('Array of leaf filters or level-1 groups'),
  })
  .describe('Nested filter structure (max 2 levels). Omit to operate on all rows.');
export type RootFilter = z.infer<typeof rootFilterSchema>;
